import type { BarChatState } from './state-mapping';

/**
 * One color decision per bar state, expressed as a Tailwind `text-*` class —
 * every dot/ring/icon then just adds `bg-current` / `stroke-current` /
 * `fill-current` to pick it up, so there is exactly one place that decides
 * "asking is amber" instead of three maps that could quietly drift apart.
 *
 * Green/red are the app's `success`/`destructive` tokens and amber is the
 * canonical `amber-500` — both per the CLAUDE.md one-green/one-red/
 * canonical-amber rule, same tone `chat-light.tsx` already uses for the
 * identical states elsewhere in the app. `running`'s brand blue is the one
 * addition this design brings in (`--eddy-blue`, declared in `top-bar.css`,
 * scoped under the `.eddy-topbar` class every top-bar root carries).
 * `earlier` (the quiet tail) gets no state color at all — plain ink.
 */
export const STATE_COLOR_CLASS: Record<BarChatState, string> = {
  asking: 'text-amber-500',
  running: 'text-[var(--eddy-blue)]',
  ready: 'text-success',
  stalled: 'text-destructive',
  earlier: 'text-[var(--ink-3)]',
};

/** The board's group headings, in the same fixed order `groupBarChats`
 *  produces groups in. */
export const STATE_GROUP_LABEL: Record<BarChatState, string> = {
  asking: 'Asking you',
  running: 'Running',
  ready: 'Done',
  stalled: 'Stalled · needs a fix',
  earlier: 'Earlier',
};

/** The status cluster's wording for each state — shorter than the group
 *  headings above (the heading may qualify the state, e.g. "Stalled · needs a
 *  fix"), and the exact word the cluster uses in its hover tip and its
 *  accessible name, so the bar and the board never name one thing two ways. */
export const STATE_PILL_LABEL: Record<'asking' | 'running' | 'ready' | 'stalled', string> = {
  asking: 'asking you',
  running: 'running',
  ready: 'done',
  stalled: 'stalled',
};

/** The board row's hover action chip — matches `actionVerbFor` in
 *  `state-mapping.ts`: amber TEXT for "Answer", neutral otherwise. Never an
 *  orange background (eddy, 2026-10-26). */
export function actionChipClass(state: BarChatState): string {
  if (state === 'asking') return 'border border-[var(--line)] bg-[var(--bg-1)] text-amber-600 dark:text-amber-400';
  return 'border border-[var(--line)] bg-[var(--bg-1)] text-[var(--ink-1)]';
}
